import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Building2,
  Receipt,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';

// Info tooltip component
function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block ml-1">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs bg-popover border border-border rounded-lg shadow-lg w-48 text-center">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
        </div>
      )}
    </div>
  );
}

export function DashboardPage() {
  const { selectedYear, taxSummary, fetchTaxSummary, isLoading } = useTaxStore();

  useEffect(() => {
    fetchTaxSummary(selectedYear);
  }, [selectedYear, fetchTaxSummary]);

  const stats = [
    {
      name: 'Total Income',
      value: taxSummary?.income?.total || 0,
      icon: DollarSign,
      tooltip: 'All income sources combined: wages, self-employment, investments, and other earnings.',
    },
    {
      name: 'Tax Liability',
      value: taxSummary?.tax?.total || 0,
      icon: Receipt,
      tooltip: 'Total federal taxes owed including income tax and self-employment tax.',
    },
    {
      name: 'Total Deductions',
      value: taxSummary?.deductions?.amount || 0,
      icon: TrendingDown,
      tooltip: 'Amount subtracted from income before calculating taxes (standard or itemized).',
    },
    {
      name: 'Effective Rate',
      value: parseFloat(taxSummary?.summary?.effectiveTaxRate || '0'),
      icon: TrendingUp,
      isPercent: true,
      tooltip: 'Your actual tax rate: total tax divided by total income. Usually lower than your marginal rate.',
    },
  ];

  const quickActions = [
    { name: 'Upload Documents', href: '/documents', icon: FileText, color: 'bg-blue-500/10 text-blue-500' },
    { name: 'Add Business', href: '/businesses', icon: Building2, color: 'bg-purple-500/10 text-purple-500' },
    { name: 'View Deductions', href: '/deductions', icon: Receipt, color: 'bg-green-500/10 text-green-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/20 rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-2">
          Welcome to TaxVault
        </h2>
        <p className="text-muted-foreground">
          Your tax year {selectedYear} overview.
          {taxSummary?.summary?.isRefund
            ? ` You're on track for a ${formatCurrency(taxSummary.refundOrOwed)} refund!`
            : taxSummary?.refundOrOwed
            ? ` You may owe ${formatCurrency(Math.abs(taxSummary.refundOrOwed))}.`
            : ' Upload documents to get started.'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-card border border-border rounded-xl p-5 card-hover"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground flex items-center">
                {stat.name}
                {stat.tooltip && <InfoTooltip text={stat.tooltip} />}
              </span>
              <div className="p-2 rounded-lg bg-primary/10">
                <stat.icon className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <span className="text-2xl font-bold">
                {stat.isPercent ? formatPercent(stat.value) : formatCurrency(stat.value)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Income Breakdown */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Income Breakdown</h3>
          <div className="space-y-4">
            {[
              { name: 'Wages (W-2)', value: taxSummary?.income?.wages || 0, color: 'bg-blue-500' },
              { name: 'Self-Employment', value: taxSummary?.income?.selfEmployment || 0, color: 'bg-purple-500' },
              { name: 'Capital Gains', value: taxSummary?.income?.capitalGains || 0, color: 'bg-green-500' },
              { name: 'Dividends', value: taxSummary?.income?.dividends || 0, color: 'bg-cyan-500' },
              { name: 'Staking/Rewards', value: taxSummary?.income?.cryptoIncome || 0, color: 'bg-orange-500' },
            ].filter((item) => item.value !== 0 || item.name === 'Wages (W-2)' || item.name === 'Self-Employment').map((item) => {
              const total = taxSummary?.income?.total || 1;
              const percent = (item.value / total) * 100;
              return (
                <div key={item.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{item.name}</span>
                    <span className="font-medium">{formatCurrency(item.value)}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-500`}
                      style={{ width: `${Math.max(percent, 1)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-smooth group"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${action.color}`}>
                    <action.icon className="h-4 w-4" />
                  </div>
                  <span className="font-medium">{action.name}</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-smooth" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Tax Status */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Tax Year Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { name: 'Documents', count: taxSummary?.breakdown?.documentCount || 0, status: taxSummary?.breakdown?.documentCount > 0 },
            { name: 'W-2 Forms', count: taxSummary?.breakdown?.w2Count || 0, status: taxSummary?.breakdown?.w2Count > 0 },
            { name: 'Businesses', count: taxSummary?.breakdown?.businessCount || 0, status: true },
            { name: 'Investments', count: taxSummary?.breakdown?.investmentTransactions || 0, status: true },
          ].map((item) => (
            <div
              key={item.name}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
            >
              {item.status ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              <div>
                <p className="font-medium">{item.name}</p>
                <p className="text-sm text-muted-foreground">{item.count} added</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Refund/Owed Banner */}
      {taxSummary && (
        <div
          className={`rounded-xl p-6 ${
            taxSummary.summary?.isRefund
              ? 'bg-green-500/10 border border-green-500/20'
              : 'bg-yellow-500/10 border border-yellow-500/20'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {taxSummary.summary?.isRefund ? 'Estimated Refund' : 'Estimated Amount Owed'}
              </p>
              <p className="text-3xl font-bold">
                {formatCurrency(Math.abs(taxSummary.refundOrOwed))}
              </p>
            </div>
            <Link
              to="/tax-summary"
              className="px-4 py-2 bg-background border border-border rounded-lg font-medium hover:bg-muted transition-smooth"
            >
              View Details
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
